import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { mockMemorials } from '../data/mockData';
import MemorialVisitorInsights from '../components/Dashboard/MemorialVisitorInsights';

const {
  FiEye, FiHeart, FiMessageSquare, FiUsers,
  FiTrendingUp, FiSettings, FiEdit, FiShare2,
  FiArrowLeft, FiMoreVertical,
} = FiIcons;

const Dashboard = () => {
  // Uses first memorial as demo dashboard — not param-dependent
  const memorial = mockMemorials[0];

  const stats = [
    { label: 'Total Visits',    value: '1,284', change: '+12%', icon: FiEye,          color: 'text-blue-500' },
    { label: 'Candles Lit',     value: '452',   change: '+5%',  icon: FiHeart,         color: 'text-red-500' },
    { label: 'Tributes Left',   value: '86',    change: '+8%',  icon: FiMessageSquare, color: 'text-accent-gold' },
    { label: 'Unique Visitors', value: '912',   change: '+15%', icon: FiUsers,         color: 'text-accent-navy' },
  ];

  return (
    <div className="pt-24 min-h-screen bg-brand-light pb-20">
      <div className="container mx-auto px-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-10">
          <div className="flex items-center gap-4">
            <Link to={`/memorial/${memorial.id}`} className="p-3 bg-white rounded-2xl border border-brand-dark hover:text-accent-gold transition-colors">
              <SafeIcon icon={FiArrowLeft} className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-3xl font-serif text-accent-navy">Analytics Dashboard</h1>
              <p className="text-text-muted flex items-center gap-2">
                Managing: <span className="font-bold text-accent-navy">{memorial.name}</span>
              </p>
            </div>
          </div>
          <div className="flex gap-3 w-full md:w-auto">
            <Link to={`/memorial/${memorial.id}`} className="flex-grow md:flex-none btn-secondary flex items-center justify-center gap-2 py-2.5">
              <SafeIcon icon={FiEdit} /> Edit Page
            </Link>
            <button className="flex-grow md:flex-none btn-primary flex items-center justify-center gap-2 py-2.5">
              <SafeIcon icon={FiShare2} /> Share Link
            </button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
          {stats.map((stat, idx) => (
            <motion.div key={stat.label} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.1 }}
              className="bg-white p-6 rounded-3xl shadow-sm border border-brand-dark group hover:border-accent-gold transition-all">
              <div className="flex justify-between items-start mb-4">
                <div className={`p-3 rounded-2xl bg-brand-light ${stat.color} group-hover:scale-110 transition-transform`}>
                  <SafeIcon icon={stat.icon} className="w-6 h-6" />
                </div>
                <span className="text-green-500 text-xs font-bold flex items-center gap-1 bg-green-50 px-2 py-1 rounded-lg">
                  <SafeIcon icon={FiTrendingUp} /> {stat.change}
                </span>
              </div>
              <h3 className="text-3xl font-serif text-accent-navy mb-1">{stat.value}</h3>
              <p className="text-text-muted text-sm font-medium">{stat.label}</p>
            </motion.div>
          ))}
        </div>

        <div className="mb-10">
          <MemorialVisitorInsights memorial={memorial} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2 space-y-10">
            <div className="bg-white p-8 rounded-[32px] shadow-sm border border-brand-dark">
              <div className="flex justify-between items-center mb-8">
                <h3 className="text-xl font-serif text-accent-navy">Visitor Activity (Last 30 Days)</h3>
                <select className="bg-brand-light border-none rounded-xl px-4 py-2 text-sm font-bold text-accent-navy outline-none">
                  <option>Daily</option>
                  <option>Weekly</option>
                </select>
              </div>
              <div className="h-64 flex items-end gap-2 px-2">
                {[40, 70, 45, 90, 65, 80, 50, 85, 100, 75, 60, 95].map((height, i) => (
                  <motion.div key={i} initial={{ height: 0 }} animate={{ height: `${height}%` }}
                    transition={{ delay: i * 0.05, duration: 1 }}
                    className="flex-grow bg-accent-navy/10 hover:bg-accent-gold transition-colors rounded-t-lg relative group">
                    <div className="absolute -top-10 left-1/2 -translate-x-1/2 bg-accent-navy text-white text-[10px] px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                      {height * 10} Views
                    </div>
                  </motion.div>
                ))}
              </div>
              <div className="flex justify-between mt-6 text-[10px] font-bold text-text-muted uppercase tracking-widest border-t border-brand-light pt-4">
                <span>Week 1</span><span>Week 2</span><span>Week 3</span><span>Week 4</span>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-white p-8 rounded-[32px] shadow-sm border border-brand-dark">
                <h3 className="text-xl font-serif text-accent-navy mb-6">Top Locations</h3>
                <div className="space-y-4">
                  <LocationRow city="London, UK"    count="342" percent={45} />
                  <LocationRow city="New York, US"  count="218" percent={30} />
                  <LocationRow city="Sydney, AU"    count="156" percent={20} />
                  <LocationRow city="Others"        count="38"  percent={5}  />
                </div>
              </div>
              <div className="bg-white p-8 rounded-[32px] shadow-sm border border-brand-dark">
                <h3 className="text-xl font-serif text-accent-navy mb-6">Device Usage</h3>
                <div className="flex items-center justify-center h-40">
                  <div className="relative w-32 h-32 rounded-full border-[12px] border-brand-light flex items-center justify-center">
                    <div className="absolute inset-0 rounded-full border-[12px] border-accent-gold border-t-transparent border-r-transparent -rotate-45" />
                    <div className="text-center">
                      <p className="text-2xl font-bold text-accent-navy">65%</p>
                      <p className="text-[10px] text-text-muted uppercase font-bold">Mobile</p>
                    </div>
                  </div>
                </div>
                <div className="flex justify-center gap-6 mt-6">
                  <div className="flex items-center gap-2 text-xs font-bold">
                    <span className="w-3 h-3 rounded-full bg-accent-gold" /> Mobile
                  </div>
                  <div className="flex items-center gap-2 text-xs font-bold">
                    <span className="w-3 h-3 rounded-full bg-brand-light border border-brand-dark" /> Desktop
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-8">
            <div className="bg-accent-navy text-white p-8 rounded-[32px] shadow-xl">
              <div className="flex items-center gap-3 mb-6">
                <SafeIcon icon={FiSettings} className="text-accent-gold w-6 h-6" />
                <h3 className="font-serif text-xl">Quick Controls</h3>
              </div>
              <div className="space-y-4">
                <ControlButton label="Moderate Tributes" count="3 New" active />
                <ControlButton label="Update Service Times" />
                <ControlButton label="Manage Gallery" />
                <ControlButton label="Privacy Settings" />
              </div>
            </div>

            <div className="bg-white p-8 rounded-[32px] shadow-sm border border-brand-dark">
              <h3 className="text-xl font-serif text-accent-navy mb-6">Recent Activity</h3>
              <div className="space-y-6">
                <ActivityItem icon={FiHeart}         title="New Candle Lit"   desc="Anonymous from London"        time="2m ago" />
                <ActivityItem icon={FiMessageSquare} title="New Tribute"      desc="Sarah Jenkins left a message" time="1h ago" />
                <ActivityItem icon={FiEye}           title="Traffic Spike"    desc="15 people viewing now"        time="4h ago" />
              </div>
              <button className="w-full mt-8 py-3 border-t border-brand-light text-accent-gold text-sm font-bold hover:text-accent-navy transition-colors">
                View Full Log
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const LocationRow = ({ city, count, percent }) => (
  <div className="space-y-1.5">
    <div className="flex justify-between text-sm font-bold">
      <span className="text-accent-navy">{city}</span>
      <span className="text-text-muted">{count}</span>
    </div>
    <div className="h-1.5 bg-brand-light rounded-full overflow-hidden">
      <div className="h-full bg-accent-gold rounded-full" style={{ width: `${percent}%` }} />
    </div>
  </div>
);

const ControlButton = ({ label, count, active = false }) => (
  <button className="w-full flex justify-between items-center group p-1">
    <span className={`text-sm ${active ? 'text-white font-bold' : 'text-white/60 hover:text-white'} transition-colors`}>{label}</span>
    {count ? (
      <span className="bg-accent-gold text-accent-navy text-[10px] px-2 py-0.5 rounded-full font-black uppercase">{count}</span>
    ) : (
      <SafeIcon icon={FiMoreVertical} className="text-white/30 group-hover:text-white" />
    )}
  </button>
);

const ActivityItem = ({ icon, title, desc, time }) => (
  <div className="flex gap-4">
    <div className="w-10 h-10 rounded-xl bg-brand-light flex items-center justify-center text-accent-gold flex-shrink-0">
      <SafeIcon icon={icon} className="w-5 h-5" />
    </div>
    <div className="flex-grow">
      <div className="flex justify-between items-start">
        <h4 className="text-sm font-bold text-accent-navy">{title}</h4>
        <span className="text-[10px] text-text-muted uppercase font-bold">{time}</span>
      </div>
      <p className="text-xs text-text-muted">{desc}</p>
    </div>
  </div>
);

export default Dashboard;